import { useEffect, useState } from "react";

import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PermissionPicker } from "@/features/rbac/components/PermissionPicker";
import { useCreateRole, useUpdateRole } from "@/features/rbac/hooks/useRoles";
import { GLOBAL_WILDCARD } from "@/features/rbac/permissions/catalog";
import type { PermissionOrWildcard } from "@/features/rbac/permissions/catalog";
import { BACKSTOP_ROLE_ID } from "@/features/rbac/permissions/systemRoles";
import type { RbacRole } from "@/features/rbac/schemas/rbac";

interface RoleFormDialogProps {
  /** Rol a editar, o `null` para crear uno nuevo. */
  role: RbacRole | null;
  open: boolean;
  onClose: () => void;
}

export function RoleFormDialog({ role, open, onClose }: RoleFormDialogProps) {
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState<PermissionOrWildcard[]>([]);

  useEffect(() => {
    if (!open) return;
    setName(role?.name ?? "");
    setDescription(role?.description ?? "");
    setPermissions([...(role?.permissions ?? [])] as PermissionOrWildcard[]);
  }, [open, role]);

  const isEditing = role !== null;
  const isBackstop = role?.id === BACKSTOP_ROLE_ID;
  const isSaving = createRole.isPending || updateRole.isPending;

  const handleSubmit = async () => {
    try {
      if (isEditing) {
        await updateRole.mutateAsync({
          roleId: role.id,
          data: { name, description, permissions },
        });
        toast.success(`Rol "${name}" actualizado`);
      } else {
        await createRole.mutateAsync({ name, description, permissions });
        toast.success(`Rol "${name}" creado`);
      }
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "No se pudo guardar el rol"
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{isEditing ? `Editar «${role.name}»` : "Nuevo rol"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "El identificador del rol no se puede cambiar: las asignaciones apuntan a él."
              : "El identificador se deriva del nombre y queda fijo al crearlo."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="role-name">Nombre</Label>
            <Input
              id="role-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Auditoría de estudios"
            />
            {isEditing && (
              <p className="text-xs text-muted-foreground">
                Identificador: <code>{role.id}</code>
              </p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="role-description">Descripción</Label>
            <Input
              id="role-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Qué puede hacer quien tenga este rol"
            />
          </div>

          <div className="grid gap-2">
            <Label>Permisos</Label>
            {isBackstop ? (
              <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                El rol Administrador debe conservar el acceso total: es la garantía de
                que nunca se pierda el acceso a la administración de roles. Sus permisos
                no se pueden editar.
              </p>
            ) : (
              <PermissionPicker value={permissions} onChange={setPermissions} />
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            disabled={
              isSaving ||
              name.trim().length === 0 ||
              (!isBackstop && permissions.length === 0)
            }
          >
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? "Guardar cambios" : "Crear rol"}
          </Button>
        </div>

        {!isBackstop && permissions.includes(GLOBAL_WILDCARD) && (
          <p className="text-xs text-muted-foreground">
            Este rol concede acceso total, incluidas las funcionalidades que se añadan
            en el futuro.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
